/*
  Task notes are rich text (notes_html). Anything stored there is untrusted (pasted from e-mails, written by
  older clients), so it is cleaned before it is shown or saved:
    • only simple formatting tags are kept (paragraphs, line breaks, bold/italic/underline, lists, headings,
      quotes, links); everything else is unwrapped to its text, and script-like elements are dropped whole;
    • every attribute is removed except a link's href, which must be http(s) or mailto — links open in a
      new tab with rel="noopener noreferrer".
  No dependency and no CDN script (the old editor loaded Quill from jsDelivr at runtime).
*/

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'A', 'DIV', 'SPAN',
]);
const DROPPED_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'FRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT', 'SVG', 'MATH', 'IMG', 'PICTURE',
  'VIDEO', 'AUDIO', 'SOURCE', 'CANVAS', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'LINK', 'META', 'BASE', 'TITLE', 'HEAD',
]);
const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

const cleanChildren = (parent) => {
  [...parent.childNodes].forEach((node) => {
    if (node.nodeType === 3) return; // text
    if (node.nodeType !== 1) { // comments, processing instructions…
      node.remove();
      return;
    }
    const tag = node.tagName.toUpperCase();
    if (DROPPED_TAGS.has(tag)) {
      node.remove();
      return;
    }
    cleanChildren(node);
    if (!ALLOWED_TAGS.has(tag)) {
      node.replaceWith(...node.childNodes);
      return;
    }
    const href = tag === 'A' ? String(node.getAttribute('href') || '').trim() : '';
    [...node.attributes].forEach((attribute) => node.removeAttribute(attribute.name));
    if (tag === 'A') {
      if (href && SAFE_HREF.test(href)) {
        node.setAttribute('href', href);
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });
};

/** Clean HTML for display / saving ('' for empty notes). */
export function sanitizeNotesHtml(html) {
  const source = String(html || '').trim();
  if (!source || typeof DOMParser === 'undefined') return '';
  // An inert document: scripts never run and images never load in it.
  const parsed = new DOMParser().parseFromString(source, 'text/html');
  cleanChildren(parsed.body);
  const cleaned = parsed.body.innerHTML.trim();
  return notesToText(cleaned) ? cleaned : '';
}

/** The words of the notes (for the description / search). */
export function notesToText(html) {
  const source = String(html || '');
  if (!source || typeof DOMParser === 'undefined') return '';
  const parsed = new DOMParser().parseFromString(source, 'text/html');
  parsed.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  // A block starts and ends a line ("per room<div>DALI…</div>" from the editor is two lines, not "per roomDALI").
  parsed.querySelectorAll('p, li, div, h1, h2, h3, blockquote').forEach((block) => {
    block.before('\n');
    block.append('\n');
  });
  return String(parsed.body.textContent || '').replace(/[ \t]*\n\s*/g, '\n').trim();
}
